import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDealership } from '../../contexts/DealershipContext';
import { useEmployees } from '../../contexts/EmployeeContext';
import { useCart } from '../../contexts/CartContext';
import UserContext from '../../contexts/UserContext';
import { calculateLineItemTotals } from '../../utils/pricing';
import useIsAdmin from '../../hooks/useIsAdmin';
import LiveProof from '../../components/LiveProof';
import ProofBuilder from '../../components/ProofBuilder';
import { PROOF_SPECS } from '../../proofSpecs';
import "./ProductDetailPage.css";

const ProductDetailPage = () => {
  const { productId } = useParams();
  const history = useHistory();

  const {
    productDetails: product,
    getProductDetails,
    productLoading: loading,
    productError: error,
    dealership
  } = useDealership();

  const {
    employees,
    loading: employeesLoading,
    error: employeesError,
    fetchEmployees
  } = useEmployees();

  const { user } = useContext(UserContext);
  const isAdmin = useIsAdmin();
  const [builderOpen, setBuilderOpen] = useState(false);

  const {
    addToCart,
    updateOrderDetails
  } = useCart();

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [message, setMessage] = useState('');

  const [variantSelectOpen, setVariantSelectOpen] = useState(false);
  const [packageSelectOpen, setPackageSelectOpen] = useState(false);
  const [employeeSelectOpen, setEmployeeSelectOpen] = useState(false);


  const variantSelectRef = useRef(null);
  const packageSelectRef = useRef(null);
  const employeeSelectRef = useRef(null);

  // Toggling functions
  const toggleVariantSelect = () => {
    setVariantSelectOpen(!variantSelectOpen);
    setPackageSelectOpen(false);
    setEmployeeSelectOpen(false);
  };

  const togglePackageSelect = () => {
    setPackageSelectOpen(!packageSelectOpen);
    setVariantSelectOpen(false);
    setEmployeeSelectOpen(false);
  };

  // Add this to your toggle functions
  const toggleEmployeeSelect = () => {
    setEmployeeSelectOpen(!employeeSelectOpen);
    setVariantSelectOpen(false);
    setPackageSelectOpen(false);
  };

  // Add this to your handler functions
  const handleEmployeeSelectAndClose = (e) => {
    handleEmployeeSelect(e);
    setEmployeeSelectOpen(false);
  };

  // Handle select and close functions
  const handleVariantSelectAndClose = (e) => {
    handleVariantSelect(e);
    setVariantSelectOpen(false);
  };

  const handlePackageSelectAndClose = (e) => {
    handlePackageSelect(e);
    setPackageSelectOpen(false);
  };

  // Add this helper function to determine dropdown size based on number of options
  const getDropdownSize = (options) => {
    // Limit to the number of options (max 6)
    return Math.min(options.length, 6);
  };


  // Fetch product details when component mounts
  useEffect(() => {
    if (productId) {
      getProductDetails(productId);
    }
  }, [productId, getProductDetails]);

  // Fetch employees when dealership is available
  useEffect(() => {
    if (dealership?.bac) {
      fetchEmployees(dealership.bac);
    }
  }, [dealership?.bac]);

  // Show packages tagged to the selected variant + universal packages (no variantId)
  const filteredPackages = useMemo(() => {
    if (!product?.packages?.length) return [];
    const sorted = [...product.packages].sort((a, b) => (a.price || 0) - (b.price || 0));
    if (!selectedVariant) return sorted;
    return sorted.filter(pkg => !pkg.variantId || String(pkg.variantId) === String(selectedVariant.id));
  }, [product?.packages, selectedVariant]);

  // Set initial variant when product loads
  useEffect(() => {
    if (product?.variants?.length > 0) {
      setSelectedVariant(product.variants[0]);
    }
  }, [product]);

  // Auto-select first package whenever the filtered list changes
  useEffect(() => {
    setSelectedPackage(filteredPackages.length > 0 ? filteredPackages[0] : null);
  }, [filteredPackages]);

  // Close package dropdown on outside click
  useEffect(() => {
    if (!packageSelectOpen) return;
    const handleOutside = (e) => {
      if (packageSelectRef.current && !packageSelectRef.current.contains(e.target)) {
        setPackageSelectOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [packageSelectOpen]);

  // Automatically select first employee when employees load
  useEffect(() => {
    if (employees && employees.length > 0) {
      setSelectedEmployee(employees[0]);
    }
  }, [employees]);

  const handlePackageSelect = (e) => {
    const packageId = e.target.value;
    const selectedPkg = product.packages.find(pkg =>
      pkg.id === packageId || pkg.id === String(packageId)
    );
    setSelectedPackage(selectedPkg);
  };

  const handleVariantSelect = (e) => {
    const variantId = e.target.value;
    const selectedVar = product.variants.find(variant =>
      variant.id === variantId || variant.id === String(variantId)
    );
    setSelectedVariant(selectedVar);
  };

  const handleEmployeeSelect = (e) => {
    const employeeId = e.target.value;
    const selectedEmp = employees.find(emp =>
      emp.id === employeeId || emp.id === String(employeeId)
    );
    setSelectedEmployee(selectedEmp);
  };

  const calculateTotal = () => {
    const { total } = calculateLineItemTotals({
      price: selectedPackage?.price,
      fee: selectedPackage?.fee,
      quantity,
    });
    return total;
  };

  const handleAddToCart = async () => {
    try {
      const pricing = calculateLineItemTotals({
        price: selectedPackage?.price,
        fee: selectedPackage?.fee,
        quantity,
      });
      // Construct line item with updated price using package price
      const lineItem = {
        itemId: product.id,
        packageId: selectedPackage?.id,
        variantId: selectedVariant?.id,
        quantity: pricing.quantity,
        price: pricing.price,
        fee: pricing.fee,
        markup: pricing.markup,
        total: pricing.total,
        employeeId: selectedEmployee?.id,
        name: product.name,
        image: product.image
      };

      addToCart(lineItem);

      updateOrderDetails({
        name: dealership?.name || '',
        email: user?.email || '',
        phone: dealership?.salesPhone || '',
        message,
        status: 'DRAFT'
      });
    } catch (err) {
      // Handle error appropriately (e.g., show error message)
    }
  };

  const handleBackToProducts = () => {
    history.push('/promotions');
  };

  if (loading) return <div className="loading">Loading product details...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!product) return <div className="not-found">Product not found</div>;

  return (
    <div className="product-detail-container">
      <button className="back-button" onClick={handleBackToProducts}>
        ← Back to Products
      </button>

      <div className="product-detail">
        <div className="product-image-container">
          {PROOF_SPECS[String(product.id)] ? (
            <LiveProof
              product={product}
              dealership={dealership}
              productId={String(product.id)}
              variantImage={selectedVariant?.image}
            />
          ) : (selectedVariant?.image || product.image) ? (
            <img
              src={selectedVariant?.image || product.image}
              alt={product.name}
              className="product-detail-image"
            />
          ) : (
            <div className="no-image">No image available</div>
          )}

          {isAdmin && (
            <button
              className="configure-proof-btn"
              onClick={() => setBuilderOpen(o => !o)}
            >
              {builderOpen ? 'Close Proof Builder' : 'Configure Proof'}
            </button>
          )}
          {isAdmin && builderOpen && (
            <ProofBuilder product={product} dealership={dealership} variantImage={selectedVariant?.image} />
          )}
        </div>

        <div className="product-info">
          <h1>{product.name}</h1>
          {product.creativeType && (
            <div className="product-type">
              Type: {product.creativeType.name}
            </div>
          )}
          {product.description && (
            <div className="product-type">
              {product.description}
            </div>
          )}


          {/* Variant Select - Updated to handle dynamic sizing */}
          {product.variants && product.variants.length > 0 && (
            <div className="selection-group">
              <label htmlFor="variant-select">Select Variant:</label>
              <div className="dropdown-container" ref={variantSelectRef}>
                <div
                  className={`dropdown-select dropdown-select-closed ${variantSelectOpen ? 'active' : ''}`}
                  onClick={toggleVariantSelect}
                >
                  {selectedVariant ? selectedVariant.name : 'Select a variant'}
                </div>

                {variantSelectOpen && (
                  <select
                    id="variant-select"
                    value={selectedVariant ? selectedVariant.id : ''}
                    onChange={handleVariantSelectAndClose}
                    className="dropdown-select dropdown-select-open"
                    size={getDropdownSize(product.variants)}
                    autoFocus
                    onBlur={() => setVariantSelectOpen(false)}
                  >
                    {product.variants.map(variant => (
                      <option key={variant.id} value={variant.id}>
                        {variant.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          )}


          {product.employeeInfo === true && (
            <div className="selection-group">
              <label htmlFor="employee-select">Select Employee:</label>
              {employeesLoading ? (
                <div>Loading employees...</div>
              ) : employeesError ? (
                <div className="error">{employeesError}</div>
              ) : employees.length === 0 ? (
                <div>No employees available</div>
              ) : (
                <div className="dropdown-container" ref={employeeSelectRef}>
                  <div
                    className={`dropdown-select dropdown-select-closed ${employeeSelectOpen ? 'active' : ''}`}
                    onClick={toggleEmployeeSelect}
                  >
                    {selectedEmployee ? selectedEmployee.name || `Employee ${selectedEmployee.id}` : 'Select an employee'}
                  </div>

                  {employeeSelectOpen && (
                    <select
                      id="employee-select"
                      value={selectedEmployee ? selectedEmployee.id : ''}
                      onChange={handleEmployeeSelectAndClose}
                      className="dropdown-select dropdown-select-open"
                      size={getDropdownSize(employees)}
                      autoFocus
                      onBlur={() => setEmployeeSelectOpen(false)}
                    >
                      {employees.map(employee => (
                        <option key={employee.id} value={employee.id}>
                          {employee.name || `Employee ${employee.id}`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
          )}



          {/* Package Select - Updated to handle dynamic sizing */}
          {product.packages && product.packages.length > 0 && (
            <div className="selection-group">
              <label>{selectedVariant ? `Packages for ${selectedVariant.name}:` : 'Choose a Package:'}</label>
              <div className="dropdown-container" ref={packageSelectRef}>
                <div
                  className={`dropdown-select dropdown-select-closed ${packageSelectOpen ? 'active' : ''}`}
                  onClick={togglePackageSelect}
                >
                  {selectedPackage
                    ? `${selectedPackage.name} — $${selectedPackage.price || 0}`
                    : 'Select a package'}
                </div>

                {packageSelectOpen && (
                  <div className="dropdown-option-list">
                    {filteredPackages.map(pkg => (
                      <div
                        key={pkg.id}
                        className={`dropdown-option-item${selectedPackage?.id === pkg.id ? ' selected' : ''}`}
                        onMouseDown={() => { setSelectedPackage(pkg); setPackageSelectOpen(false); }}
                      >
                        <span className="dropdown-option-name">{pkg.name}</span>
                        <span className="dropdown-option-price">${pkg.price || 0} · Fee: {pkg.fee || 0}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}


          {/* {/\* Quantity Selection *\/} */}
          {/* <div className="selection-group"> */}
          {/*   <label htmlFor="quantity-select">Quantity:</label> */}
          {/*   <div className="dropdown-container" ref={quantitySelectRef}> */}
          {/*     <div */}
          {/*       className={`dropdown-select dropdown-select-closed ${quantitySelectOpen ? 'active' : ''}`} */}
          {/*       onClick={toggleQuantitySelect} */}
          {/*     > */}
          {/*       {quantity} */}
          {/*     </div> */}

          {/*     {quantitySelectOpen && ( */}
          {/*       <select */}
          {/*         id="quantity-select" */}
          {/*         value={quantity} */}
          {/*         onChange={handleQuantitySelectAndClose} */}
          {/*         className="dropdown-select dropdown-select-open" */}
          {/*         size={6} */}
          {/*         autoFocus */}
          {/*         onBlur={() => setQuantitySelectOpen(false)} */}
          {/*       > */}
          {/*         {quantityOptions.map(num => ( */}
          {/*           <option key={num} value={num}> */}
          {/*             {num} */}
          {/*           </option> */}
          {/*         ))} */}
          {/*       </select> */}
          {/*     )} */}
          {/*   </div> */}
          {/* </div> */}

          <div className="product-actions">
            <div className="product-price">
              ${calculateTotal().toFixed(2)}
            </div>
            <button
              className="add-to-cart-button"
              onClick={handleAddToCart}
              /* disabled={!selectedEmployee} */
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
